import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Plus, X, ChevronLeft, ChevronRight, Heart, Eye } from "lucide-react";

export default function StoriesBar({ onCreateStory }) {
  const { user } = useAuth();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingStory, setViewingStory] = useState(null);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    fetchStories();
  }, []);

  useEffect(() => {
    if (viewingStory) {
      const timer = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            // Move to next story
            handleNextStory();
            return 0;
          }
          return prev + 2;
        });
      }, 100);
      return () => clearInterval(timer);
    }
  }, [viewingStory, currentStoryIndex]);

  const fetchStories = async () => {
    try {
      const response = await axios.get(`${API}/stories`);
      setStories(response.data);
    } catch (error) {
      console.error("Failed to fetch stories:", error);
    } finally {
      setLoading(false);
    }
  };

  const openStory = async (userStories, index = 0) => {
    setViewingStory(userStories);
    setCurrentStoryIndex(index);
    setProgress(0);

    // Mark as viewed
    if (userStories.stories[index]) {
      try {
        await axios.get(`${API}/stories/${userStories.stories[index].id}`);
      } catch (error) {
        console.error("Failed to mark story as viewed:", error);
      }
    }
  };

  const handleNextStory = () => {
    if (!viewingStory) return;

    if (currentStoryIndex < viewingStory.stories.length - 1) {
      setCurrentStoryIndex(currentStoryIndex + 1);
      setProgress(0);
    } else {
      // Move to next user's stories
      const currentUserIndex = stories.findIndex((s) => s.user_id === viewingStory.user_id);
      if (currentUserIndex < stories.length - 1) {
        openStory(stories[currentUserIndex + 1], 0);
      } else {
        closeStory();
      }
    }
  };

  const handlePrevStory = () => {
    if (!viewingStory) return;

    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1);
      setProgress(0);
    } else {
      // Move to previous user's stories
      const currentUserIndex = stories.findIndex((s) => s.user_id === viewingStory.user_id);
      if (currentUserIndex > 0) {
        const prevUser = stories[currentUserIndex - 1];
        openStory(prevUser, prevUser.stories.length - 1);
      }
    }
  };

  const closeStory = () => {
    setViewingStory(null);
    setCurrentStoryIndex(0);
    setProgress(0);
    fetchStories(); // Refresh to update seen status
  };

  const reactToStory = async (storyId) => {
    try {
      await axios.post(`${API}/stories/${storyId}/react?reaction_type=heart`);
    } catch (error) {
      console.error("Failed to react to story:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4 px-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex-shrink-0 animate-pulse">
            <div className="w-16 h-16 rounded-full bg-amber-100"></div>
          </div>
        ))}
      </div>
    );
  }

  const currentStory = viewingStory?.stories[currentStoryIndex];

  return (
    <>
      {/* Stories Bar */}
      <div className="flex gap-4 overflow-x-auto pb-4 px-1 scrollbar-hide">
        {/* Create Story Button */}
        <button
          onClick={onCreateStory}
          className="flex-shrink-0 flex flex-col items-center gap-1"
        >
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center border-2 border-white shadow-lg">
            <Plus className="w-8 h-8 text-white" />
          </div>
          <span className="text-xs text-gray-600">Add Story</span>
        </button>

        {/* User Stories */}
        {stories.map((userStory) => (
          <button
            key={userStory.user_id}
            onClick={() => openStory(userStory)}
            className="flex-shrink-0 flex flex-col items-center gap-1"
          >
            <div
              className={`w-16 h-16 rounded-full p-0.5 ${
                userStory.has_unseen
                  ? "bg-gradient-to-br from-amber-400 via-orange-500 to-pink-500"
                  : "bg-gray-300"
              }`}
            >
              <div className="w-full h-full rounded-full bg-white p-0.5">
                <div className="w-full h-full rounded-full bg-amber-100 flex items-center justify-center overflow-hidden">
                  {userStory.user_photo ? (
                    <img
                      src={userStory.user_photo}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-amber-600 font-bold text-lg">
                      {userStory.user_name?.[0]}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <span className="text-xs text-gray-600 max-w-16 truncate">
              {userStory.user_id === user?.id ? "Your Story" : userStory.user_name?.split(" ")[0]}
            </span>
          </button>
        ))}
      </div>

      {/* Story Viewer Modal */}
      {viewingStory && currentStory && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          {/* Progress Bars */}
          <div className="absolute top-4 left-4 right-4 flex gap-1 z-10">
            {viewingStory.stories.map((_, idx) => (
              <div key={idx} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-all duration-100"
                  style={{
                    width:
                      idx < currentStoryIndex
                        ? "100%"
                        : idx === currentStoryIndex
                        ? `${progress}%`
                        : "0%",
                  }}
                />
              </div>
            ))}
          </div>

          {/* User Info */}
          <div className="absolute top-8 left-4 right-4 flex items-center justify-between z-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                {viewingStory.user_photo ? (
                  <img src={viewingStory.user_photo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-bold">{viewingStory.user_name?.[0]}</span>
                )}
              </div>
              <div>
                <div className="text-white font-semibold">{viewingStory.user_name}</div>
                <div className="text-white/70 text-xs">
                  {new Date(currentStory.created_at).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            </div>
            <button onClick={closeStory} className="text-white p-2">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Story Content */}
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: currentStory.background_color || "#000" }}
          >
            {currentStory.media_type === "image" ? (
              <img
                src={currentStory.media_url}
                alt=""
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <video
                src={currentStory.media_url}
                autoPlay
                muted
                className="max-w-full max-h-full object-contain"
              />
            )}
            {currentStory.text && (
              <div className="absolute bottom-32 left-4 right-4 text-center">
                <p className="text-white text-xl font-medium drop-shadow-lg">
                  {currentStory.text}
                </p>
              </div>
            )}
          </div>

          {/* Navigation Areas */}
          <button
            onClick={handlePrevStory}
            className="absolute left-0 top-0 bottom-0 w-1/3 z-10"
          />
          <button
            onClick={handleNextStory}
            className="absolute right-0 top-0 bottom-0 w-1/3 z-10"
          />

          {/* Story Stats & Actions */}
          <div className="absolute bottom-8 left-4 right-4 flex items-center justify-between z-10">
            <div className="flex items-center gap-4 text-white/80 text-sm">
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {currentStory.views?.length || 0} views
              </span>
            </div>
            <button
              onClick={() => reactToStory(currentStory.id)}
              className="p-3 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
            >
              <Heart className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
